import type { LifecycleStatusEnum, OrdersListParams, TravelStatusEnum } from '@gta/api-client'
import { z } from 'zod'

/**
 * 订单域 search params 契约(URL 是唯一状态源)。
 *
 * 取值面严格对齐生成物:
 *  - `status` / `travel_status` 的枚举取自 `LifecycleStatusEnum` / `TravelStatusEnum`;
 *  - 参数名与 `OrdersListParams` 一一同名,列表页可直接把 search 转成请求参数,不做映射层。
 * 非法值一律 `catch` 成兜底而不是抛错 —— 手改 URL / 旧链接不能把页面打崩。
 */

export const ORDER_LIFECYCLE_VALUES = [
  'pending_payment',
  'confirmed',
  'cancelled',
  'closed',
] as const satisfies readonly LifecycleStatusEnum[]

export const ORDER_TRAVEL_VALUES = [
  'not_started',
  'ready',
  'traveling',
  'returned',
] as const satisfies readonly TravelStatusEnum[]

/** 详情页 tab 也是有意义状态,必须进 URL(冷加载还原到同一张 tab)。 */
export const ORDER_DETAIL_TABS = ['items', 'payment', 'history', 'travelers'] as const

export type OrderDetailTab = (typeof ORDER_DETAIL_TABS)[number]

/**
 * YYYY-MM-DD;后端 `created_after` / `created_before` 只认这个格式,非法值直接丢弃。
 * 光靠正则不够 —— `2026-13-99` 能过正则却会让后端 400,这里连日历合法性一起校。
 */
function isRealCalendarDate(value: string): boolean {
  const [year, month, day] = value.split('-').map(Number)
  if (year === undefined || month === undefined || day === undefined) return false
  const date = new Date(Date.UTC(year, month - 1, day))
  return (
    date.getUTCFullYear() === year && date.getUTCMonth() === month - 1 && date.getUTCDate() === day
  )
}

const isoDate = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/)
  .refine(isRealCalendarDate)
  .optional()
  .catch(undefined)

export const DEFAULT_ORDERS_PAGE = 1

export const ordersSearchSchema = z.object({
  /** 页码(1 基);默认第一页,`page=1` 由 stripSearchParams 摘掉,不往 URL 里塞冗余键。 */
  page: z.coerce.number().int().min(1).default(DEFAULT_ORDERS_PAGE).catch(DEFAULT_ORDERS_PAGE),
  /** 生命周期轴。 */
  status: z.enum(ORDER_LIFECYCLE_VALUES).optional().catch(undefined),
  /** 出行轴。 */
  travel_status: z.enum(ORDER_TRAVEL_VALUES).optional().catch(undefined),
  /** 关键词:订单资源号 / 客户名(后端 icontains)。 */
  search: z.string().trim().min(1).optional().catch(undefined),
  /** 签约日期区间(闭区间,按日期比较)。 */
  created_after: isoDate,
  created_before: isoDate,
})

export type OrdersSearch = z.infer<typeof ordersSearchSchema>

/** 等于默认值的参数不回写 URL(stripSearchParams;A4 全站统一)。 */
export const ORDERS_SEARCH_DEFAULTS = {
  page: DEFAULT_ORDERS_PAGE,
} satisfies Partial<OrdersSearch>

export const DEFAULT_ORDER_DETAIL_TAB: OrderDetailTab = 'items'

export const orderDetailSearchSchema = z.object({
  tab: z.enum(ORDER_DETAIL_TABS).default(DEFAULT_ORDER_DETAIL_TAB).catch(DEFAULT_ORDER_DETAIL_TAB),
})

export type OrderDetailSearch = z.infer<typeof orderDetailSearchSchema>

export const ORDER_DETAIL_SEARCH_DEFAULTS = {
  tab: DEFAULT_ORDER_DETAIL_TAB,
} satisfies Partial<OrderDetailSearch>

/** 每页条数:后端 PageNumberPagination 默认 20(max 100),这里显式带上便于分页算总页数。 */
export const ORDERS_PAGE_SIZE = 20

/** 空串 / 空白一律折成 undefined —— URL 里不留 `?search=` 这种噪声键。 */
function blankToUndefined(value: string | undefined): string | undefined {
  const trimmed = value?.trim()
  return trimmed && trimmed.length > 0 ? trimmed : undefined
}

/**
 * 把一次筛选变更合并进现有 search。
 * 约定:除非 patch 里显式带了 `page`,任何筛选变更都回到第一页(否则会停在不存在的页码上)。
 * 「等于默认值的键不进 URL」不再手写丢键 —— 交给路由上的 stripSearchParams 统一处理(A4)。
 */
export function applyOrdersSearchPatch(prev: OrdersSearch, patch: Partial<OrdersSearch>): OrdersSearch {
  const merged: OrdersSearch = { ...prev, ...patch }
  return {
    ...merged,
    page: 'page' in patch ? merged.page : DEFAULT_ORDERS_PAGE,
    search: blankToUndefined(merged.search),
  }
}

/** search params → 生成 hook 的 query 参数(同名直传,只补分页)。 */
export function toOrdersListParams(search: OrdersSearch): OrdersListParams {
  return {
    page: search.page,
    page_size: ORDERS_PAGE_SIZE,
    ...(search.status ? { status: search.status } : {}),
    ...(search.travel_status ? { travel_status: search.travel_status } : {}),
    ...(search.search ? { search: search.search } : {}),
    ...(search.created_after ? { created_after: search.created_after } : {}),
    ...(search.created_before ? { created_before: search.created_before } : {}),
  }
}

/** 是否有任何筛选生效(决定「重置」按钮与空态文案)。 */
export function hasOrdersFilter(search: OrdersSearch): boolean {
  return Boolean(
    search.status ?? search.travel_status ?? search.search ?? search.created_after ?? search.created_before,
  )
}
